import React, { useState } from "react";
import { Menu, Send, Activity, BookOpen, Trophy, FolderOpen, ShieldCheck, CalendarClock, GraduationCap } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { Logo } from "@/components/common/Brand";
import { LangMenu } from "@/components/common/LangMenu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const L = {
  results: { hu: "Eredményeink", en: "Our results", de: "Ergebnisse", fr: "Résultats", cs: "Výsledky" },
  cases: { hu: "Ügyeim", en: "My cases", de: "Meine Fälle", fr: "Mes dossiers", cs: "Moje případy" },
  menu: { hu: "Menü", en: "Menu", de: "Menü", fr: "Menu", cs: "Nabídka" },
  avail: { hu: "Elérhetőség", en: "Availability", de: "Erreichbarkeit", fr: "Disponibilité", cs: "Dostupnost" },
};

export function SiteHeader() {
  const { lang, tr } = useLang();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const l = (k) => L[k][lang] || L[k].en;

  const scrollTo = (id) => {
    if (pathname !== "/") { navigate("/"); setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300); return; }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  const go = (fn) => { setOpen(false); setTimeout(fn, 150); };

  const items = [
    ["report", tr.navReport, Send, () => scrollTo("report")],
    ["how", tr.navHow, BookOpen, () => scrollTo("how")],
    ["track", tr.navTrack, Activity, () => scrollTo("track")],
    ["my-cases", l("cases"), FolderOpen, () => navigate("/my-cases"), "/my-cases"],
    ["availability", l("avail"), CalendarClock, () => navigate("/availability"), "/availability"],
    ["results", l("results"), Trophy, () => navigate("/results"), "/results"],
    ["academy", "Reporter Academy", GraduationCap, () => navigate("/academy"), "/academy"],
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-zinc-800/70 bg-zinc-950/85 backdrop-blur">
      <div className="mx-auto flex h-24 max-w-6xl items-center justify-between px-5">
        <button onClick={() => scrollTo("top")} className="flex items-center" data-testid="site-brand">
          <Logo className="h-16" />
        </button>
        <div className="flex items-center gap-2">
          <LangMenu />
          <button data-testid="site-menu-btn" onClick={() => setOpen(true)} aria-label={l("menu")}
            className="grid h-10 w-10 place-items-center rounded-md border border-zinc-800 bg-zinc-900/60 text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-zinc-50">
            <Menu size={20} />
          </button>
        </div>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[300px] border-zinc-800 bg-[#0c0c0e] text-zinc-100" data-testid="site-menu">
          <SheetHeader>
            <SheetTitle className="text-left text-zinc-50">{l("menu")}</SheetTitle>
            <SheetDescription className="sr-only">{l("menu")}</SheetDescription>
          </SheetHeader>
          <nav className="mt-6 flex flex-col gap-1">
            {items.map(([id, label, Icon, fn, path]) => (
              <button key={id} data-testid={`nav-${id}`} onClick={() => go(fn)}
                className={`flex items-center gap-3 rounded-lg px-3 py-3 text-left text-[14px] font-medium transition-colors hover:bg-zinc-900 ${path && pathname === path ? "bg-zinc-900 text-[#FFCC00]" : "text-zinc-300 hover:text-zinc-50"}`}>
                <Icon size={17} className="text-zinc-500" /> {label}
              </button>
            ))}
            <div className="my-3 h-px bg-zinc-800" />
            <button data-testid="nav-moderator" onClick={() => go(() => navigate("/moderator"))}
              className="flex items-center gap-3 rounded-lg px-3 py-3 text-left text-[13px] text-zinc-500 transition-colors hover:bg-zinc-900 hover:text-zinc-200">
              <ShieldCheck size={16} /> {tr.mLogin}
            </button>
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
}
